import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Share, MoreVertical, PlusSquare, ExternalLink } from 'lucide-react';
import Sheet from '../common/Sheet';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';

export interface InstallAppButtonProps {
  /** Shorter copy for in-app placements (My Day). */
  variant?: 'login' | 'inline';
  className?: string;
  /** Link the sheet to the full install guide (/install). Off on the guide page itself. */
  showGuideLink?: boolean;
}

/**
 * "Install pelora app" (Phase 16A, design §4). Phones and tablets only: offline mode needs the
 * installed app. Android Chrome / Samsung Internet open the native dialog; iPhone/iPad and Firefox
 * get short instructions; embedded browsers are told to open pelora in Chrome or Safari.
 * Hidden on laptops and desktop browsers and once the app is installed.
 */
const InstallAppButton: React.FC<InstallAppButtonProps> = ({ variant = 'login', className = '', showGuideLink = true }) => {
  const { mode, promptInstall } = useInstallPrompt();
  const [sheet, setSheet] = useState<'ios' | 'menu' | null>(null);

  if (mode === 'desktop' || mode === 'installed') return null;

  if (mode === 'in-app') {
    return (
      <p className={`flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 ${className}`}>
        <ExternalLink size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        To install pelora for offline use, open this page in Chrome (Android) or Safari (iPhone / iPad).
      </p>
    );
  }

  const onClick = async () => {
    if (mode === 'ios') setSheet('ios');
    else if (mode === 'prompt') await promptInstall();
    else setSheet('menu'); // Firefox, or Chrome before its install event fired: the browser menu installs too
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className={`flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 ${className}`}
      >
        <Download size={18} aria-hidden="true" />
        {variant === 'login' ? 'Install pelora app' : 'Install for offline use'}
      </button>
      {variant === 'login' && <p className="mt-1 text-center text-xs text-slate-500">Lets you chart visits without signal.</p>}

      <Sheet open={sheet !== null} onClose={() => setSheet(null)} title="Install pelora" subtitle="Add pelora to this device to use it offline">
        {sheet === 'ios' ? (
          <ol className="space-y-4 text-base text-gray-800">
            <li className="flex items-start gap-3"><Share size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Tap the <strong>Share</strong> button in Safari's toolbar.</li>
            <li className="flex items-start gap-3"><PlusSquare size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
            <li className="flex items-start gap-3"><Download size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Open pelora from your Home Screen and sign in.</li>
          </ol>
        ) : (
          <ol className="space-y-4 text-base text-gray-800">
            <li className="flex items-start gap-3"><MoreVertical size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Open the browser <strong>menu</strong> (⋮).</li>
            <li className="flex items-start gap-3"><PlusSquare size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Choose <strong>Install</strong> (or <strong>Add to Home screen</strong>).</li>
            <li className="flex items-start gap-3"><Download size={22} className="mt-0.5 shrink-0 text-sky-600" aria-hidden="true" />Open pelora from the app icon and sign in.</li>
          </ol>
        )}
        {showGuideLink && (
          <Link to="/install" onClick={() => setSheet(null)} className="mt-6 inline-flex min-h-[44px] items-center text-sm font-medium text-sky-700 underline">
            Full instructions for Android and iPhone / iPad
          </Link>
        )}
      </Sheet>
    </>
  );
};

export default InstallAppButton;
